import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getDbHeader } from "../../utils/util";

/**
 * Props:
 * - title: string
 * - onSelectionChange: function(selectedArray) => void
 *    Called whenever the user selects/unselects categories
 * - id: string | number
 */

const CategorySelectorAccordions = ({ title = "", onSelectionChange, id }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const abortController = useMemo(() => new AbortController(), []);

  // Fetch categories with cleanup (unchanged)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}event/get-product-categories`,
          {
            ...getDbHeader(id),
            signal: abortController.signal,
          }
        );
        setData(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error(err);
          setError("Failed to load categories");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => abortController.abort();
  }, [abortController]);

  // Memoized selection updater (unchanged)
  const handleSelectionUpdate = useCallback(
    (newSelections) => {
      setSelectedCategories(newSelections);
      onSelectionChange?.(newSelections);
    },
    [onSelectionChange]
  );

  // Category check used by double/triple (unchanged)
  const isCategorySelected = useCallback(
    (category) =>
      selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      ),
    [selectedCategories]
  );

  // Toggle used by double/triple (unchanged)
  const handleCheckboxChange = useCallback(
    (category) => {
      const exists = selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );

      handleSelectionUpdate(
        exists
          ? selectedCategories.filter(
              (sel) =>
                !(
                  sel.MainCategory === category.MainCategory &&
                  (sel.Category ?? null) === (category.Category ?? null) &&
                  (sel.SubCategory ?? null) === (category.SubCategory ?? null)
                )
            )
          : [...selectedCategories, category]
      );
    },
    [selectedCategories, handleSelectionUpdate]
  );

  // Memoized grouped data (unchanged)
  const { mainGroups, categoryGroups } = useMemo(() => {
    if (!data?.categories) return { mainGroups: {}, categoryGroups: {} };

    const groups = data.categories.reduce((acc, cat) => {
      const main = cat.MainCategory || "Unknown";
      if (!acc[main]) acc[main] = [];
      acc[main].push(cat);
      return acc;
    }, {});

    const catGroups =
      data.type === "triple"
        ? data.categories.reduce((acc, item) => {
            const key = `${item.MainCategory}-${item.Category}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {})
        : {};

    return { mainGroups: groups, categoryGroups: catGroups };
  }, [data]);

  // ── SINGLE type ──────────────────────────────────────────────────────────────

  // One option per unique MainCategory
  const singleOptions = useMemo(() => {
    if (data?.type !== "single") return [];
    return Object.keys(mainGroups).map((main) => ({
      MainCategory: main,
      Category: null,
      SubCategory: null,
    }));
  }, [data, mainGroups]);

  // Current Autocomplete value — only single-type entries
  const singleValue = useMemo(
    () =>
      selectedCategories.filter(
        (sel) => sel.Category === null && sel.SubCategory === null
      ),
    [selectedCategories]
  );

  const handleSingleChange = useCallback(
    (_event, newValue) => {
      handleSelectionUpdate(newValue);
    },
    [handleSelectionUpdate]
  );

  // ── DOUBLE / TRIPLE render helpers (unchanged) ───────────────────────────────

  const renderDouble = (items) =>
    items.map((cat, idx) => (
      <FormControlLabel
        sx={{ width: "100%" }}
        key={`${cat.MainCategory}-${cat.Category}-${idx}`}
        control={
          <Checkbox
            checked={isCategorySelected(cat)}
            onChange={() => handleCheckboxChange(cat)}
          />
        }
        label={<Typography variant="body2">{cat.Category || "N/A"}</Typography>}
      />
    ));

  const renderTriple = (mainCategory) =>
    Object.entries(categoryGroups)
      .filter(([key]) => key.startsWith(mainCategory))
      .map(([key, items]) => (
        <Box key={key} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            {key.split("-")[1]}
          </Typography>
          {items.map((subItem, subIdx) => (
            <FormControlLabel
              sx={{ width: "100%" }}
              key={`${key}-${subIdx}`}
              control={
                <Checkbox
                  checked={isCategorySelected(subItem)}
                  onChange={() => handleCheckboxChange(subItem)}
                />
              }
              label={
                <Typography variant="body2">
                  {subItem.SubCategory || "N/A"}
                </Typography>
              }
            />
          ))}
        </Box>
      ));

  // ── UI states (unchanged) ────────────────────────────────────────────────────

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data?.categories?.length)
    return <Alert severity="info">No categories available</Alert>;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {data.type === "single" ? (
        /* ── Multi-select Autocomplete for SINGLE ── */
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={singleOptions}
          value={singleValue}
          getOptionLabel={(option) => option.MainCategory}
          isOptionEqualToValue={(option, value) =>
            option.MainCategory === value.MainCategory
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.MainCategory}
            </li>
          )}
          onChange={handleSingleChange}
          renderInput={(params) => (
            <TextField {...params} label="Select Main Categories" />
          )}
        />
      ) : (
        /* ── Accordion layout for DOUBLE / TRIPLE ── */
        Object.entries(mainGroups).map(([mainCategory, items]) => (
          <Accordion key={`${mainCategory}-${data.type}`}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                {mainCategory}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {data.type === "double" && renderDouble(items)}
              {data.type === "triple" && renderTriple(mainCategory)}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default React.memo(CategorySelectorAccordions);
